/*
 * Day-opening carry-over — seed the napnyitás cash input with the previous
 * day-close "Bentmaradt készpénz" (the ZÁRÓ / FINAL cash left in the drawer).
 *
 * At close, both napzárás flows persist the remaining drawer cash under the
 * `dayClosed_amount` AsyncStorage key (DayClosingScreenExtended: the computed
 * ZÁRÓ (10-11) / FINAL; DayClosingScreenBasic: the counted final amount). That
 * key survives the close→open reset (handleConfirm only wipes the `dayOpened_*`
 * / `dayOpening_id` keys), so the next day's opening screen reads it here to
 * pre-fill the cash field — which the cashier can still overwrite. Falls back to
 * an empty input (→ 0) when there is no usable prior amount on this terminal.
 *
 * Offline-first & terminal-local: mirrors how DayClosingScreenExtended already
 * treats `dayClosed_amount` as the next day's starting float. The pure
 * normalizer is split out so it is unit-testable without the AsyncStorage
 * runtime; the async reader is a thin best-effort wrapper.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';

/** AsyncStorage key holding the last day-close remaining cash (Bentmaradt készpénz). */
export const DAY_CLOSED_AMOUNT_KEY = 'dayClosed_amount';

/**
 * Normalize a raw stored day-close remaining-cash value into a pinpad-ready
 * opening-cash string. Returns `''` whenever there is no usable positive amount
 * so the day-opening input defaults to empty (the cashier starts from 0).
 *
 * @param {*} raw Raw AsyncStorage value — a stringified number, a number, or null.
 * @returns {string} Integer HUF amount as a string, or `''` when absent / zero /
 *   negative / non-numeric.
 */
export function normalizeCarryOverOpeningCash(raw) {
    if (raw === null || raw === undefined) {
        return '';
    }

    const amount = Number(String(raw).trim().replace(',', '.'));

    if (!Number.isFinite(amount) || amount <= 0) {
        return '';
    }

    return String(Math.round(amount));
}

/**
 * Read the previous day-closing "Bentmaradt készpénz" to pre-fill the
 * day-opening cash input. Best-effort — never throws; resolves to `''` on any
 * error so the opening flow always renders.
 *
 * @returns {Promise<string>} Pinpad-ready cash string, or `''` when unavailable.
 */
export async function getCarryOverOpeningCash() {
    try {
        const raw = await AsyncStorage.getItem(DAY_CLOSED_AMOUNT_KEY);

        return normalizeCarryOverOpeningCash(raw);
    } catch (carryOverReadError) {
        console.error('Day-opening carry-over read failed', carryOverReadError);

        return '';
    }
}

export default {
    DAY_CLOSED_AMOUNT_KEY,
    normalizeCarryOverOpeningCash,
    getCarryOverOpeningCash,
};
